import React, { useMemo } from "react";

const ANCHO = 190;
const ALTO = 66;
const SEP_X = 100;
const SEP_Y = 34;
const MARGEN_X = 40;
const MARGEN_Y = 80;

const C = {
  caja: "#0A1222",
  borde: "#1B2A44",
  texto: "#E8EEF8",
  tenue: "#8FA0BC",
  celeste: "#64ADF2",
  amarillo: "#F2C063",
  rojo: "#FF9B9B",
  linea: "#3A5378",
};

const recortar = (t, n) => (t.length > n ? `${t.slice(0, n - 1)}…` : t);

//los links entre escenas son elementos tipo 2 que guardan el TITULO de la escena destino
export const analizarRecorrido = (escenas) => {
  const porTitulo = new Map();
  escenas.forEach((e, i) => {
    if (!porTitulo.has(e.titulo)) porTitulo.set(e.titulo, i);
  });

  const salidas = escenas.map(() => new Set());
  const rotos = escenas.map(() => 0);
  escenas.forEach((e, i) => {
    (e.elementos || []).forEach((el) => {
      if (!el || Number(el.tipo) !== 2) return;
      const destino = porTitulo.get(el.escena);
      if (destino === undefined) rotos[i] += 1;
      else if (destino !== i) salidas[i].add(destino);
    });
  });

  let inicio = escenas.findIndex((e) => e.principal);
  if (inicio < 0) inicio = escenas.length ? 0 : -1;

  //BFS desde la escena principal para ubicar cada escena en una columna
  const nivel = escenas.map(() => -1);
  const orden = [];
  if (inicio >= 0) {
    nivel[inicio] = 0;
    const cola = [inicio];
    while (cola.length) {
      const actual = cola.shift();
      orden.push(actual);
      salidas[actual].forEach((d) => {
        if (nivel[d] === -1) {
          nivel[d] = nivel[actual] + 1;
          cola.push(d);
        }
      });
    }
  }
  const maxNivel = Math.max(0, ...nivel);
  const sinAcceso = [];
  escenas.forEach((_, i) => {
    if (nivel[i] === -1) sinAcceso.push(i);
  });

  return { salidas, rotos, inicio, nivel, orden, maxNivel, sinAcceso };
};

export const MapaRecorrido = ({ escenas, escenaActual, onSeleccionar }) => {
  const datos = useMemo(() => {
    const a = analizarRecorrido(escenas);
    const columnas = [];
    a.orden.forEach((i) => {
      (columnas[a.nivel[i]] = columnas[a.nivel[i]] || []).push(i);
    });
    const pos = {};
    columnas.forEach((col, c) =>
      col.forEach((i, f) => {
        pos[i] = { x: MARGEN_X + c * (ANCHO + SEP_X), y: MARGEN_Y + f * (ALTO + SEP_Y) };
      })
    );
    //las escenas a las que no se llega desde el inicio van en una fila aparte, debajo del recorrido
    const filasRecorrido = Math.max(1, ...columnas.map((c) => c.length));
    const ySinAcceso = MARGEN_Y + filasRecorrido * (ALTO + SEP_Y) + 40;
    const porFila = Math.max(1, columnas.length);
    a.sinAcceso.forEach((i, k) => {
      pos[i] = {
        x: MARGEN_X + (k % porFila) * (ANCHO + SEP_X),
        y: ySinAcceso + Math.floor(k / porFila) * (ALTO + SEP_Y),
      };
    });
    const filasSinAcceso = Math.ceil(a.sinAcceso.length / porFila);

    //un par de escenas enlazadas en ambos sentidos se dibuja como una sola linea con dos flechas
    const aristas = [];
    const vistas = new Set();
    a.salidas.forEach((destinos, o) =>
      destinos.forEach((d) => {
        const clave = o < d ? `${o}-${d}` : `${d}-${o}`;
        if (vistas.has(clave)) return;
        vistas.add(clave);
        aristas.push({ o, d, doble: a.salidas[d].has(o) });
      })
    );

    const cols = Math.max(1, columnas.length);
    return {
      ...a,
      pos,
      aristas,
      ancho: MARGEN_X * 2 + cols * ANCHO + (cols - 1) * SEP_X + 60,
      alto: (filasSinAcceso ? ySinAcceso + filasSinAcceso * (ALTO + SEP_Y) : MARGEN_Y + filasRecorrido * (ALTO + SEP_Y)) + 20,
      ySinAcceso: filasSinAcceso ? ySinAcceso : -1,
    };
  }, [escenas]);

  if (!escenas.length) {
    return (
      <div className="inm-mapa" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p className="inm-tenue">Todavía no hay escenas en esta experiencia.</p>
      </div>
    );
  }

  const camino = ({ o, d }) => {
    const a = datos.pos[o];
    const b = datos.pos[d];
    if (!a || !b) return "";
    if (a.x === b.x) {
      //misma columna: curva por la derecha
      const x = a.x + ANCHO;
      const y1 = a.y + ALTO / 2;
      const y2 = b.y + ALTO / 2;
      return `M ${x} ${y1} C ${x + 50} ${y1}, ${x + 50} ${y2}, ${x} ${y2}`;
    }
    const izq = a.x < b.x ? a : b;
    const der = a.x < b.x ? b : a;
    const x1 = izq.x + ANCHO;
    const y1 = izq.y + ALTO / 2;
    const x2 = der.x;
    const y2 = der.y + ALTO / 2;
    const m = (x2 - x1) / 2;
    const linea = `M ${x1} ${y1} C ${x1 + m} ${y1}, ${x2 - m} ${y2}, ${x2} ${y2}`;
    if (a.x < b.x) return linea;
    //el sentido real va de derecha a izquierda: invertimos el trazo para que la flecha quede bien
    return `M ${x2} ${y2} C ${x2 - m} ${y2}, ${x1 + m} ${y1}, ${x1} ${y1}`;
  };

  const sinSalida = (i) => datos.salidas[i].size === 0;

  return (
    <div className="inm-mapa">
      <div className="inm-mapa__leyenda" aria-label="Referencias">
        <span>
          <i style={{ width: 10, height: 10, borderRadius: 3, border: `2px solid ${C.celeste}` }} /> Inicio
        </span>
        <span>
          <i style={{ width: 8, height: 8, borderRadius: "50%", background: C.amarillo }} /> Sin salida
        </span>
        <span>
          <i style={{ width: 8, height: 8, borderRadius: "50%", background: C.rojo }} /> Sin acceso o enlace roto
        </span>
        <span style={{ color: C.tenue }}>Hacé clic en una escena para editarla</span>
      </div>
      <svg
        width={datos.ancho}
        height={datos.alto}
        role="img"
        aria-label={`Mapa del recorrido con ${escenas.length} escenas`}
        style={{ display: "block", marginTop: -48 }}
      >
        <defs>
          <marker id="inm-flecha" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0 0 L10 5 L0 10 z" fill={C.linea} />
          </marker>
        </defs>

        {datos.ySinAcceso >= 0 && (
          <text
            x={MARGEN_X}
            y={datos.ySinAcceso - 14}
            fill={C.rojo}
            fontSize="12"
            fontFamily="Inter, sans-serif"
          >
            Sin acceso desde el inicio
          </text>
        )}

        {datos.aristas.map((ar) => (
          <path
            key={`${ar.o}-${ar.d}`}
            d={camino(ar)}
            fill="none"
            stroke={C.linea}
            strokeWidth="1.6"
            markerEnd="url(#inm-flecha)"
            markerStart={ar.doble ? "url(#inm-flecha)" : undefined}
          />
        ))}

        {escenas.map((e, i) => {
          const p = datos.pos[i];
          if (!p) return null;
          const actual = i === escenaActual;
          const esInicio = i === datos.inicio;
          const sinAcceso = datos.nivel[i] === -1;
          const avisos = [];
          if (sinSalida(i)) avisos.push({ color: C.amarillo, texto: "Sin salida" });
          if (datos.rotos[i]) avisos.push({ color: C.rojo, texto: `${datos.rotos[i]} enlace${datos.rotos[i] > 1 ? "s" : ""} roto${datos.rotos[i] > 1 ? "s" : ""}` });
          return (
            <g
              key={e.id || i}
              className="inm-mapa__nodo"
              transform={`translate(${p.x} ${p.y})`}
              role="button"
              tabIndex={0}
              aria-label={`Escena ${e.titulo}${esInicio ? ", inicio" : ""}${sinAcceso ? ", sin acceso" : ""}`}
              onClick={() => onSeleccionar(i)}
              onKeyDown={(ev) => {
                if (ev.key === "Enter" || ev.key === " ") {
                  ev.preventDefault();
                  onSeleccionar(i);
                }
              }}
            >
              <title>{e.titulo}</title>
              <rect
                className="inm-mapa__caja"
                width={ANCHO}
                height={ALTO}
                rx="12"
                fill={actual ? "#0F2757" : C.caja}
                stroke={esInicio || actual ? C.celeste : sinAcceso ? C.rojo : C.borde}
                strokeWidth={esInicio || actual ? 2 : 1.2}
                strokeDasharray={sinAcceso ? "5 4" : undefined}
              />
              <text x="14" y="26" fill={C.texto} fontSize="14" fontWeight="600" fontFamily="'League Spartan', Inter, sans-serif">
                {recortar(e.titulo || "Sin título", 24)}
              </text>
              <text x="14" y="47" fontSize="11.5" fontFamily="Inter, sans-serif" fill={C.tenue}>
                {esInicio ? <tspan fill={C.celeste}>Inicio · </tspan> : null}
                {datos.salidas[i].size} salida{datos.salidas[i].size === 1 ? "" : "s"}
              </text>
              {avisos.map((av, k) => (
                <g key={av.texto} transform={`translate(${ANCHO - 12 - k * 14} 14)`}>
                  <circle r="4.5" fill={av.color}>
                    <title>{av.texto}</title>
                  </circle>
                </g>
              ))}
            </g>
          );
        })}
      </svg>
    </div>
  );
};
